/**
 * ContactsListPage — table view over vault_business_contacts with the
 * Leads-private extension fields (lead_score / last_contacted_at)
 * surfaced alongside. Filters: search + lifecycle stage + company.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Copy } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsContacts,
  useFiasLeadsCompanies,
  useCreateFiasLeadsContact,
  useFiasLeadsCrossArchePresence,
} from '../hooks/useFiasLeads';
import { SkeletonRow } from '../components/Skeletons';
import { CrossArcheBadge } from '../components/CrossArcheBadge';
import { DedupDialog } from '../components/DedupDialog';

interface Props {
  holderId: string;
}

export function ContactsListPage({ holderId }: Props) {
  const [companyId, setCompanyId] = useState<string>('');
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDedup, setShowDedup] = useState(false);
  const [draft, setDraft] = useState({ firstName: '', lastName: '', primaryEmail: '' });

  const contactsQuery = useFiasLeadsContacts(holderId, {
    companyId: companyId || undefined,
  });
  const companiesQuery = useFiasLeadsCompanies(holderId);
  const createContact = useCreateFiasLeadsContact(holderId);
  const presenceQuery = useFiasLeadsCrossArchePresence(
    holderId,
    (contactsQuery.data ?? []).map((c) => c.contactId),
  );
  const contactPresence = presenceQuery.data?.contactPresence ?? {};

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.firstName.trim()) return;
    await createContact.mutateAsync({
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim() || undefined,
      primaryEmail: draft.primaryEmail.trim() || undefined,
      companyId: companyId || undefined,
    });
    setDraft({ firstName: '', lastName: '', primaryEmail: '' });
    setShowCreate(false);
  };

  const filtered = useMemo(() => {
    const all = contactsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (lifecycleFilter && c.lifecycleStage !== lifecycleFilter) return false;
      if (!q) return true;
      const matches =
        c.firstName.toLowerCase().includes(q) ||
        (c.lastName ?? '').toLowerCase().includes(q) ||
        (c.primaryEmail ?? '').toLowerCase().includes(q) ||
        (c.title ?? '').toLowerCase().includes(q);
      return matches;
    });
  }, [contactsQuery.data, search, lifecycleFilter]);

  const stagesSet = new Set<string>();
  for (const c of contactsQuery.data ?? []) {
    if (c.lifecycleStage) stagesSet.add(c.lifecycleStage);
  }
  const stages = [...stagesSet].sort();

  return (
    <div className="flex flex-col gap-4 p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Contacts</h1>
          <p className="text-sm text-muted-foreground">
            People you sell to. Shared with Fias 360 in this workspace.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDedup(true)}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
            title="Find duplicate contacts"
          >
            <Copy className="h-4 w-4" />
            Dedup
          </button>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
          >
            <Plus className="h-4 w-4" />
            New contact
          </button>
        </div>
      </header>

      {showDedup && <DedupDialog holderId={holderId} onClose={() => setShowDedup(false)} />}

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="flex flex-wrap gap-2 rounded border border-border bg-card p-4"
        >
          <input
            type="text"
            placeholder="First name *"
            value={draft.firstName}
            onChange={(e) => setDraft((p) => ({ ...p, firstName: e.target.value }))}
            className="flex-1 min-w-[150px] rounded border border-border bg-background px-3 py-2"
            required
            autoFocus
          />
          <input
            type="text"
            placeholder="Last name"
            value={draft.lastName}
            onChange={(e) => setDraft((p) => ({ ...p, lastName: e.target.value }))}
            className="flex-1 min-w-[150px] rounded border border-border bg-background px-3 py-2"
          />
          <input
            type="email"
            placeholder="Email"
            value={draft.primaryEmail}
            onChange={(e) => setDraft((p) => ({ ...p, primaryEmail: e.target.value }))}
            className="flex-1 min-w-[200px] rounded border border-border bg-background px-3 py-2"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.firstName.trim() || createContact.isPending}
              className="rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
            >
              {createContact.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search contacts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded border border-border bg-card pl-10 pr-3 py-2 text-sm"
          />
        </div>
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All companies</option>
          {(companiesQuery.data ?? []).map((c) => (
            <option key={c.companyId} value={c.companyId}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All stages</option>
          {stages.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {contactsQuery.isLoading && <SkeletonRow />}
      {contactsQuery.isError && (
        <p className="text-destructive text-sm">
          Failed to load contacts: {(contactsQuery.error as Error).message}
        </p>
      )}

      <div className="rounded border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-left px-4 py-2 font-medium">Title</th>
              <th className="text-left px-4 py-2 font-medium">Stage</th>
              <th className="text-left px-4 py-2 font-medium">Lead score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((c) => (
              <tr key={c.contactId} className="hover:bg-muted/30">
                <td className="px-4 py-2">
                  <Link
                    to={`${ARCHE_PATH}/contacts/${c.contactId}`}
                    className="text-foreground hover:underline"
                  >
                    {c.firstName} {c.lastName ?? ''}
                  </Link>
                  <CrossArcheBadge present={contactPresence[c.contactId] === true} />
                </td>
                <td className="px-4 py-2 text-muted-foreground">{c.primaryEmail ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground">{c.title ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground text-xs">
                  {c.lifecycleStage ?? '—'}
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {c.extension?.leadScore != null ? c.extension.leadScore.toFixed(0) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && !contactsQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            {search.trim() || companyId || lifecycleFilter
              ? 'No contacts match these filters.'
              : 'No contacts yet. Add your first one above.'}
          </p>
        )}
      </div>
    </div>
  );
}
